import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  PageHeader,
  DataTable,
  ColumnSettingsButton,
  btnPrimary,
  colors,
  input,
  label as labelStyle,
} from '../../components/ui'
import type { Column } from '../../components/ui'
import { api, ApiError } from '../../services/api'
import { useTableColumns } from '../../hooks/use-table-columns'

interface CustomerRow {
  readonly id: string
  readonly name: string
  readonly contactEmail: string | null
  readonly environmentCount: number
  readonly isArchived: boolean
}

interface CreateCustomerResponse {
  readonly organizationId: string
  readonly organizationName: string
  readonly customerAdmin: { readonly id: string; readonly email: string }
}

const columns: readonly Column<CustomerRow>[] = [
  { key: 'name', header: 'Customer', render: (r) => <strong>{r.name}</strong>, sortable: true },
  { key: 'contactEmail', header: 'Contact', render: (r) => r.contactEmail ?? '—' },
  { key: 'environmentCount', header: 'Environments', render: (r) => r.environmentCount },
]

// medflow_admin-only. Lists customer orgs and provisions new ones (each
// new customer gets an organization + an initial customer_admin user).
// The temp password is typed by the medflow_admin and handed off out of
// band -- self-signup is explicitly deferred to a later phase.
export function FleetCustomersPage() {
  const navigate = useNavigate()
  const [rows, setRows] = useState<readonly CustomerRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const { tableId: _, ...tableColumnProps } = useTableColumns('fleet-customers')
  const [form, setForm] = useState({
    orgName: '',
    contactEmail: '',
    adminEmail: '',
    firstName: '',
    lastName: '',
    password: '',
  })
  const [submitting, setSubmitting] = useState(false)
  const [createdSummary, setCreatedSummary] = useState<CreateCustomerResponse | null>(null)
  const [showArchived, setShowArchived] = useState(false)

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true)
    try {
      const res = await api.get<readonly CustomerRow[]>('/fleet/customers' + (showArchived ? '?archived=true' : ''))
      setRows(res.data ?? [])
      setLoading(false)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load customers')
      setLoading(false)
    }
  }, [showArchived])

  useEffect(() => {
    refresh()
  }, [refresh])

  async function handleCreate(e: FormEvent): Promise<void> {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      const res = await api.post<CreateCustomerResponse>('/fleet/customers', {
        organization: {
          name: form.orgName.trim(),
          contactEmail: form.contactEmail.trim() || undefined,
        },
        customerAdmin: {
          email: form.adminEmail.trim(),
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          password: form.password,
        },
      })
      if (res.data) {
        setCreatedSummary(res.data)
        setShowCreate(false)
        setForm({ orgName: '', contactEmail: '', adminEmail: '', firstName: '', lastName: '', password: '' })
        await refresh()
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create customer')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div>
      <PageHeader
        title="Customers"
        subtitle={`${rows.length} ${showArchived ? 'archived' : 'active'}`}
        actions={
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '13px' }}>
              <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
              Show archived
            </label>
            <ColumnSettingsButton columns={columns} {...tableColumnProps} />
            <button type="button" onClick={() => navigate('/fleet')}>
              Back to Fleet
            </button>
            <button type="button" style={btnPrimary} onClick={() => setShowCreate((v) => !v)}>
              {showCreate ? 'Cancel' : 'New Customer'}
            </button>
          </div>
        }
      />

      {createdSummary && (
        <div
          style={{
            background: '#dcfce7',
            border: '1px solid #bbf7d0',
            color: '#065f46',
            padding: '0.75rem',
            borderRadius: '4px',
            marginBottom: '1rem',
          }}
        >
          Created <strong>{createdSummary.organizationName}</strong>. Admin user:{' '}
          <code>{createdSummary.customerAdmin.email}</code>. Hand them the password you just set, out of band.
        </div>
      )}

      {error && (
        <div
          style={{
            color: colors.danger,
            padding: '0.75rem',
            background: colors.bgError,
            border: `1px solid ${colors.borderError}`,
            borderRadius: '3px',
            marginBottom: '1rem',
          }}
        >
          {error}
        </div>
      )}

      {showCreate && (
        <form
          onSubmit={handleCreate}
          style={{
            background: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            padding: '1.25rem',
            marginBottom: '1rem',
            maxWidth: '560px',
          }}
        >
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={labelStyle}>Organization name</label>
            <input
              type="text"
              required
              value={form.orgName}
              onChange={(e) => setForm({ ...form, orgName: e.target.value })}
              style={input}
            />
          </div>
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={labelStyle}>Contact email</label>
            <input
              type="email"
              value={form.contactEmail}
              onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
              style={input}
            />
          </div>
          <hr style={{ margin: '1rem 0', border: 'none', borderTop: `1px solid ${colors.border}` }} />
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={labelStyle}>Admin email</label>
            <input
              type="email"
              required
              value={form.adminEmail}
              onChange={(e) => setForm({ ...form, adminEmail: e.target.value })}
              style={input}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <div>
              <label style={labelStyle}>First name</label>
              <input
                type="text"
                required
                value={form.firstName}
                onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                style={input}
              />
            </div>
            <div>
              <label style={labelStyle}>Last name</label>
              <input
                type="text"
                required
                value={form.lastName}
                onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                style={input}
              />
            </div>
          </div>
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={labelStyle}>Initial password</label>
            <input
              type="password"
              required
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              style={input}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
            <button type="button" onClick={() => setShowCreate(false)}>
              Cancel
            </button>
            <button type="submit" disabled={submitting} style={btnPrimary}>
              {submitting ? 'Creating...' : 'Create Customer'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div style={{ color: colors.textMuted }}>Loading...</div>
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          keyField="id"
          emptyMessage="No customers yet."
          onRowClick={(r) => navigate('/fleet/customers/' + r.id)}
          {...tableColumnProps}
        />
      )}
    </div>
  )
}
